import React from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router';
import { InternalId, StaticReference } from '@edgebox/data-definition-kit';
import { ContentRepresentation, TaskGroupType } from '@edgebox/sync-core-data-definitions';
import { ClientTaskGroupDraft } from '@edgebox/sync-core-rest-client';
import { sendToParent } from '../../../frame-messages';
import { SyncCoreApiContext } from '../../../contexts/SyncCoreApiContext';
import {
  GovernanceAddAnotherPageButton,
  GovernanceAiSuggestedListItem,
  GovernanceAiSuggestionLabel,
  GovernanceOnboardingContainer,
  GovernanceOnboardingListItem,
  GovernanceOnboardingNewListItem,
  GovernanceOnboardingStepContainer,
} from './Onboarding/components/GovernanceOnboardingPrimitives';
import { instanceToPlain, plainToInstance } from 'class-transformer';
import { Form } from '@edgebox/react-components';
import { Field } from 'formik';
import type { FieldProps } from 'formik';
import {
  DEFAULT_SUGGESTION_REASON,
  GovernanceOnboardingPathForm,
  GovernanceOnboardingPathInput,
  OnboardingSelectedUrlSuggestion,
  getAiReasonFromSuggestion,
  getLinkNameByPath,
  isUrlWithinBase,
  normalizeConfidenceToPercent,
  normalizePageTitle,
  normalizePathInput,
  toAbsoluteUrl,
  toRelativePath,
} from './Onboarding/components/governanceOnboardingUrlSelection';
import { useBrandPresenceOnboarding } from './BrandPresenceOnboardingProvider';
import {
  BRAND_PRESENCE_CONTENT_INVENTORY_PATH,
  BRAND_PRESENCE_ONBOARDING_ROUTE_PATHS,
  BRAND_PRESENCE_ONBOARDING_TOTAL_STEPS,
  brandPresenceOnboardingStepPath,
} from './brandPresencePaths';
import { GovernanceOnboardingListSkeleton } from './Onboarding/components/GovernanceOnboardingSkeleton';

type ReviewIndexingMode = 'index-all' | 'choose' | 'later';

interface IReviewPage {
  path: string;
  title: string;
  source: 'recommended' | 'ai' | 'custom';
}

interface IAiSuggestion {
  path: string;
  title: string;
  reason: string;
  confidence: number;
  hidden: boolean;
}

interface INewPageRow {
  id: number;
  version: number;
}

export function GovernanceOnboardingStepReview() {
  const navigate = useNavigate();
  const apiContext = React.useContext(SyncCoreApiContext);
  const { recordOnboardingCompletion } = useBrandPresenceOnboarding();
  const params = useParams<{ onboardingId?: string }>();
  const [searchParams] = useSearchParams();
  const taskGroupId = params.onboardingId || searchParams.get('taskGroupId') || undefined;

  const [indexingMode, setIndexingMode] = React.useState<ReviewIndexingMode>('index-all');
  const [pages, setPages] = React.useState<IReviewPage[]>([]);
  const [suggestions, setSuggestions] = React.useState<IAiSuggestion[]>([]);
  const [recommendedPaths, setRecommendedPaths] = React.useState<Set<string>>(new Set());
  const [newPageRows, setNewPageRows] = React.useState<INewPageRow[]>([]);
  const [nextNewPageRowId, setNextNewPageRowId] = React.useState(0);
  const [editingPath, setEditingPath] = React.useState<string | undefined>(undefined);
  const [loadingChoices, setLoadingChoices] = React.useState(false);
  const [siteBaseUrl, setSiteBaseUrl] = React.useState<string | undefined>(undefined);
  const [submitting, setSubmitting] = React.useState(false);
  const [error, setError] = React.useState<string | undefined>(undefined);

  React.useEffect(() => {
    if (!apiContext?.api || !taskGroupId) {
      return;
    }

    let active = true;

    void (async () => {
      setLoadingChoices(true);
      try {
        const onboardingTaskGroup = await apiContext.api.service.taskGroups.item(taskGroupId as InternalId, true);
        const siteId = onboardingTaskGroup.site?.getId?.() || (await onboardingTaskGroup.site?.get())?.id;
        if (!siteId) {
          throw new Error('Missing onboarding site reference.');
        }

        const [freshSite, extraction, terms] = await Promise.all([
          apiContext.api.billing.sites.item(siteId as InternalId, true),
          apiContext.api.governance.onboarding.mainPageDataExtraction({ taskGroupId: taskGroupId as InternalId }),
          apiContext.api.governance.onboarding.contentItemTerms({ taskGroupId: taskGroupId as InternalId }),
        ]);

        if (!active) {
          return;
        }

        const baseUrl = String(freshSite.baseUrl || '').trim();
        if (!baseUrl) {
          throw new Error('Missing site base URL.');
        }

        setSiteBaseUrl(baseUrl);

        const recommendedAbsoluteUrls = Array.from(
          new Set(
            (terms || [])
              .map((item) => String(item.url || '').trim())
              .filter(Boolean)
              .filter((url) => isUrlWithinBase(url, baseUrl))
          )
        );

        const recommended = recommendedAbsoluteUrls.map((absoluteUrl): IReviewPage => {
          const path = toRelativePath(absoluteUrl, baseUrl) || '/';
          return {
            path,
            title: normalizePageTitle(path),
            source: 'recommended',
          };
        });

        const recommendedAbsoluteSet = new Set(recommended.map((page) => toAbsoluteUrl(page.path, baseUrl)));

        const selectedUrlSuggestions = extraction.selectedUrlSuggestions || [];
        const linkNameByPath = getLinkNameByPath(extraction.links, baseUrl);

        const mappedSuggestions = selectedUrlSuggestions
          .map((item: OnboardingSelectedUrlSuggestion): IAiSuggestion | undefined => {
            if (typeof item.url !== 'string') {
              return undefined;
            }

            if (!isUrlWithinBase(item.url, baseUrl)) {
              return undefined;
            }

            const path = toRelativePath(item.url, baseUrl) || normalizePathInput(item.url, baseUrl);
            const absoluteUrl = toAbsoluteUrl(path, baseUrl);
            if (recommendedAbsoluteSet.has(absoluteUrl)) {
              return undefined;
            }

            const titleFromLinks = path === '/' ? 'Home' : linkNameByPath.get(path);
            const titleFromSuggestionName = typeof item.name === 'string' ? item.name.trim() : undefined;

            return {
              path,
              title: normalizePageTitle(path, titleFromLinks || titleFromSuggestionName || item.url),
              reason: getAiReasonFromSuggestion(item.reasoning),
              confidence: normalizeConfidenceToPercent(item.confidence),
              hidden: false,
            };
          })
          .filter((item): item is IAiSuggestion => !!item)
          .sort((a, b) => b.confidence - a.confidence)
          .slice(0, 20);

        setPages(recommended);
        setRecommendedPaths(new Set(recommended.map((page) => page.path)));
        setSuggestions(mappedSuggestions);
      } catch (loadError) {
        if (active) {
          setError((loadError as Error)?.message || 'Unable to load URL recommendations.');
        }
      } finally {
        if (active) {
          setLoadingChoices(false);
        }
      }
    })();

    return () => {
      active = false;
    };
  }, [apiContext?.api, taskGroupId]);

  const onAcceptSuggestion = (item: IAiSuggestion) => {
    if (!siteBaseUrl) {
      setError('Missing site base URL.');
      return;
    }

    const normalizedPath = normalizePathInput(item.path, siteBaseUrl);
    setSuggestions((prev) => prev.filter((candidate) => candidate.path !== item.path));
    setPages((prev) => {
      if (prev.some((candidate) => candidate.path === normalizedPath)) {
        return prev;
      }

      return [...prev, { path: normalizedPath, title: normalizePageTitle(normalizedPath, item.title), source: 'ai' }];
    });
  };

  const onIgnoreSuggestion = (item: IAiSuggestion) => {
    setSuggestions((prev) => prev.map((candidate) => (candidate.path === item.path ? { ...candidate, hidden: true } : candidate)));
  };

  const hiddenSuggestionsCount = suggestions.filter((item) => item.hidden).length;
  const shownSuggestions = suggestions.filter((item) => !item.hidden);

  const onAddAnotherPageRow = () => {
    setNewPageRows((prev) => [...prev, { id: nextNewPageRowId, version: 0 }]);
    setNextNewPageRowId((prev) => prev + 1);
  };

  const onDeleteNewPageRow = (rowId: number) => {
    setNewPageRows((prev) => prev.filter((row) => row.id !== rowId));
  };

  return (
    <GovernanceOnboardingContainer>
      <GovernanceOnboardingStepContainer
        step={BRAND_PRESENCE_ONBOARDING_TOTAL_STEPS}
        totalSteps={BRAND_PRESENCE_ONBOARDING_TOTAL_STEPS}
        title={'Start indexing your content'}
        description={'Choose how to initialize crawling for your project and start indexing.'}
        onBack={() => {
          if (!taskGroupId) {
            return;
          }

          const backPath = brandPresenceOnboardingStepPath(BRAND_PRESENCE_ONBOARDING_ROUTE_PATHS.stepPrompts, taskGroupId);
          sendToParent({ type: 'update-path', path: backPath });
          navigate(backPath, { replace: true });
        }}
        onContinue={async () => {
          if (!taskGroupId || !apiContext?.api) {
            setError('Missing onboarding context.');
            return;
          }

          setSubmitting(true);
          setError(undefined);

          try {
            if (indexingMode === 'index-all') {
              const onboardingTaskGroup = await apiContext.api.service.taskGroups.item(taskGroupId as InternalId, true);
              const siteId = onboardingTaskGroup.site?.getId?.() || (await onboardingTaskGroup.site?.get())?.id;
              let project = await onboardingTaskGroup.project?.get();

              if (!siteId) {
                throw new Error('Missing onboarding site reference.');
              }

              if (!project?.id) {
                throw new Error('Missing onboarding project reference.');
              }

              const currentAutoCrawl = project.crawling?.autoCrawl || [];
              const nextAutoCrawl = Array.from(new Set([...currentAutoCrawl, ContentRepresentation.Page]));

              project.crawling = {
                ...(project.crawling || {}),
                autoCrawl: nextAutoCrawl,
              };

              project = await apiContext.api.service.projects.updateCrawlingSettings(project.id, instanceToPlain(project.crawling));

              // Passing in `skipOptimization` so that the content we crawled for the onboarding
              // is also crawled again for proper term extraction.
              const crawlTaskGroupDraft = new ClientTaskGroupDraft();
              crawlTaskGroupDraft.type = TaskGroupType.Crawl;
              crawlTaskGroupDraft.skipOptimization = true;
              Object.assign(crawlTaskGroupDraft, {
                site: new StaticReference({ id: siteId }),
              });

              await apiContext.api.service.taskGroups.create(crawlTaskGroupDraft);
            } else if (indexingMode === 'choose') {
              if (!siteBaseUrl) {
                throw new Error('Missing site base URL.');
              }

              const urls = Array.from(new Set(pages.map((page) => toAbsoluteUrl(page.path, siteBaseUrl))));
              if (!urls.length) {
                throw new Error('Please add at least one URL to continue.');
              }

              const outsideBaseUrl = urls.find((url) => !isUrlWithinBase(url, siteBaseUrl));
              if (outsideBaseUrl) {
                throw new Error(`URL outside of registered site base URL: ${outsideBaseUrl}`);
              }

              const crawlTaskGroupDraft = new ClientTaskGroupDraft();
              crawlTaskGroupDraft.type = TaskGroupType.Crawl;
              crawlTaskGroupDraft.skipOptimization = true;
              crawlTaskGroupDraft.urls = urls;

              await apiContext.api.service.taskGroups.create(crawlTaskGroupDraft);
            }

            // Bookkeeping the area reads to reshape itself, never a step the user waits behind: the
            // outcome lands in the provider, which is mounted above this step and outlives it, so a
            // rejected write surfaces on the content list instead of holding anyone on this screen.
            recordOnboardingCompletion(indexingMode);

            sendToParent({ type: 'update-path', path: BRAND_PRESENCE_CONTENT_INVENTORY_PATH });
            navigate(BRAND_PRESENCE_CONTENT_INVENTORY_PATH);
          } catch (submitError) {
            setError((submitError as Error)?.message || 'Unable to start indexing.');
          } finally {
            setSubmitting(false);
          }
        }}
        continueLabel={submitting ? 'Continuing…' : 'Continue'}
        continueDisabled={submitting}
      >
        <div className="w-full space-y-4 max-w-2xl mx-auto">
          {error ? <div className="text-sm text-danger">{error}</div> : null}
          <div className="rounded-xl bg-white p-5 ring-1 ring-inset ring-slate-200/80">
            <div className="space-y-4 text-sm text-slate-700">
              <Form.Check
                type="radio"
                id="governance-review-index-all"
                name="governance-review-indexing-mode"
                className="mb-0"
                checked={indexingMode === 'index-all'}
                onChange={() => !submitting && setIndexingMode('index-all')}
                label={
                  <div>
                    <span className="font-semibold text-slate-900">Index all content</span>
                    <span className="block mt-1 text-slate-500">Enable automatic page crawling and start indexing immediately.</span>
                  </div>
                }
              />

              <Form.Check
                type="radio"
                id="governance-review-let-me-choose"
                name="governance-review-indexing-mode"
                className="mb-0"
                checked={indexingMode === 'choose'}
                onChange={() => !submitting && setIndexingMode('choose')}
                label={
                  <div>
                    <span className="font-semibold text-slate-900">Let me choose</span>
                    <span className="block mt-1 text-slate-500">Choose specific URLs to crawl now.</span>
                  </div>
                }
              />

              <Form.Check
                type="radio"
                id="governance-review-decide-later"
                name="governance-review-indexing-mode"
                className="mb-0"
                checked={indexingMode === 'later'}
                onChange={() => !submitting && setIndexingMode('later')}
                label={
                  <div>
                    <span className="font-semibold text-slate-900">I will decide later</span>
                    <span className="block mt-1 text-slate-500">Skip crawl creation for now and open content inventory.</span>
                  </div>
                }
              />
            </div>
          </div>

          {indexingMode === 'choose' ? (
            <div className="space-y-4 rounded-xl bg-white p-5 ring-1 ring-inset ring-slate-200/80">
              <div className="text-sm text-slate-500">
                Select the URLs to crawl. Recommended URLs are based on your earlier key content selection.
              </div>

              {loadingChoices ? <GovernanceOnboardingListSkeleton label="Loading URL recommendations…" /> : null}

              {!loadingChoices ? (
                <div className="space-y-3">
                  {shownSuggestions.map((item) => (
                    <GovernanceAiSuggestedListItem
                      key={`suggestion-${item.path}`}
                      onAccept={() => onAcceptSuggestion(item)}
                      onIgnore={() => onIgnoreSuggestion(item)}
                    >
                      <div className="col-span-12 md:col-span-4 text-left pl-2">
                        <span className="text-primary font-bold block truncate text-sm">{item.path}</span>
                      </div>
                      <div className="col-span-12 md:col-span-6 text-left pl-2 md:pl-0">
                        <div className="flex items-center gap-2 min-w-0 flex-wrap md:flex-nowrap">
                          <div className="font-semibold text-slate-900 truncate">{item.title}</div>
                          <GovernanceAiSuggestionLabel />
                          <span className="inline-flex shrink-0 whitespace-nowrap rounded-full bg-slate-100 px-2.5 py-0.5 text-[10px] font-semibold text-slate-600 ring-1 ring-inset ring-slate-200">
                            {item.confidence}%
                          </span>
                        </div>
                        <div className="text-sm text-slate-500 truncate mt-1">{item.reason}</div>
                      </div>
                    </GovernanceAiSuggestedListItem>
                  ))}

                  {hiddenSuggestionsCount > 0 ? (
                    <div className="px-2 text-sm text-slate-500">
                      <span>{hiddenSuggestionsCount} hidden suggestions</span>
                      <button
                        type="button"
                        className="ml-2 text-primary font-semibold hover:underline"
                        onClick={() => setSuggestions((prev) => prev.map((item) => ({ ...item, hidden: false })))}
                      >
                        Show
                      </button>
                    </div>
                  ) : null}

                  {pages.map((page) =>
                    editingPath === page.path ? (
                      <GovernanceOnboardingPathForm
                        key={`edit-${page.path}`}
                        initialValues={plainToInstance(GovernanceOnboardingPathInput, { path: page.path })}
                        onSubmit={async (values) => {
                          if (!siteBaseUrl) {
                            setError('Missing site base URL.');
                            return;
                          }

                          const normalizedPath = normalizePathInput(values.path, siteBaseUrl);
                          const absolute = toAbsoluteUrl(normalizedPath, siteBaseUrl);
                          if (!isUrlWithinBase(absolute, siteBaseUrl)) {
                            setError(`URL must use site base URL: ${siteBaseUrl}`);
                            return;
                          }

                          setPages((prev) =>
                            prev.map((item) => {
                              if (item.path !== editingPath) {
                                return item;
                              }

                              return { ...item, path: normalizedPath, title: normalizePageTitle(normalizedPath, item.title) };
                            })
                          );
                          setEditingPath(undefined);
                        }}
                      >
                        {() => (
                          <GovernanceOnboardingNewListItem
                            onDelete={() => setEditingPath(undefined)}
                            onSave={() => undefined}
                            saveButtonType={'submit'}
                          >
                            <div className="col-span-12 md:col-span-10">
                              <Field name={'path'}>
                                {({ field, form }: FieldProps<string>) => (
                                  <input
                                    {...field}
                                    type={'text'}
                                    placeholder={siteBaseUrl ? `${siteBaseUrl}path or /path` : '/path'}
                                    onBlur={(event) => {
                                      if (!siteBaseUrl) {
                                        return;
                                      }
                                      const normalizedPath = normalizePathInput(event.target.value, siteBaseUrl);
                                      form.setFieldValue('path', normalizedPath, false);
                                      form.setFieldTouched('path', true, true);
                                    }}
                                    className="h-11 w-full rounded-lg border border-slate-200 bg-white px-3 text-sm text-slate-700 transition-all duration-150 focus:border-primary focus:outline-none focus:ring-4 focus:ring-primary/10"
                                  />
                                )}
                              </Field>
                            </div>
                          </GovernanceOnboardingNewListItem>
                        )}
                      </GovernanceOnboardingPathForm>
                    ) : (
                      <GovernanceOnboardingListItem
                        key={`page-${page.path}`}
                        onDelete={() => setPages((prev) => prev.filter((candidate) => candidate.path !== page.path))}
                        onEdit={() => setEditingPath(page.path)}
                      >
                        <div className="col-span-12 md:col-span-4 text-left">
                          <span className="text-primary font-bold block truncate hover:underline">{page.path}</span>
                        </div>
                        <div className="col-span-12 md:col-span-6 text-left">
                          <div className="flex items-center gap-2 min-w-0 flex-wrap md:flex-nowrap">
                            <div className="font-semibold text-slate-900 truncate">{normalizePageTitle(page.path, page.title)}</div>
                            {recommendedPaths.has(page.path) || page.source === 'recommended' ? (
                              <GovernanceAiSuggestionLabel>Recommended</GovernanceAiSuggestionLabel>
                            ) : null}
                          </div>
                        </div>
                      </GovernanceOnboardingListItem>
                    )
                  )}

                  {newPageRows.map((row) => (
                    <GovernanceOnboardingPathForm
                      key={`new-${row.id}-${row.version}`}
                      initialValues={plainToInstance(GovernanceOnboardingPathInput, { path: '' })}
                      onSubmit={async (values) => {
                        if (!siteBaseUrl) {
                          setError('Missing site base URL.');
                          return;
                        }

                        const normalizedPath = normalizePathInput(values.path, siteBaseUrl);
                        const absolute = toAbsoluteUrl(normalizedPath, siteBaseUrl);
                        if (!isUrlWithinBase(absolute, siteBaseUrl)) {
                          setError(`URL must use site base URL: ${siteBaseUrl}`);
                          return;
                        }

                        let added = false;
                        setPages((prev) => {
                          if (prev.some((candidate) => candidate.path === normalizedPath)) {
                            return prev;
                          }

                          added = true;
                          return [...prev, { path: normalizedPath, title: normalizePageTitle(normalizedPath), source: 'custom' }];
                        });

                        if (added) {
                          setNewPageRows((prev) =>
                            prev.map((candidate) =>
                              candidate.id === row.id ? { ...candidate, version: candidate.version + 1 } : candidate
                            )
                          );
                        }
                      }}
                    >
                      {() => (
                        <GovernanceOnboardingNewListItem onDelete={() => onDeleteNewPageRow(row.id)}>
                          <div className="col-span-12 md:col-span-10">
                            <Field name={'path'}>
                              {({ field, form }: FieldProps<string>) => (
                                <input
                                  {...field}
                                  type={'text'}
                                  placeholder={siteBaseUrl ? `${siteBaseUrl}path or /path` : '/path'}
                                  onBlur={(event) => {
                                    if (!siteBaseUrl) {
                                      return;
                                    }
                                    const normalizedPath = normalizePathInput(event.target.value, siteBaseUrl);
                                    form.setFieldValue('path', normalizedPath, false);
                                    form.setFieldTouched('path', true, true);
                                  }}
                                  className="h-11 w-full rounded-lg border border-slate-200 bg-white px-3 text-sm text-slate-700 transition-all duration-150 focus:border-primary focus:outline-none focus:ring-4 focus:ring-primary/10"
                                />
                              )}
                            </Field>
                          </div>
                        </GovernanceOnboardingNewListItem>
                      )}
                    </GovernanceOnboardingPathForm>
                  ))}

                  <div className="flex justify-between items-center mt-6">
                    <GovernanceAddAnotherPageButton onClick={onAddAnotherPageRow} />
                    <span className="text-sm font-bold text-slate-500">{pages.length} URLs selected</span>
                  </div>
                </div>
              ) : null}
            </div>
          ) : null}
        </div>
      </GovernanceOnboardingStepContainer>
    </GovernanceOnboardingContainer>
  );
}
